/* eslint-disable react/jsx-no-useless-fragment */
import { Card, CardContent, CircularProgress } from '@mui/material';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroller';
import NameAndChangeToScreenCard from '../../../../components/NameAndChangeToScreenCard';
import NoExamIcon from '../../../../svg/NoExamIcon';
import WritingPageIcon from '../../../../svg/WritingPageIcon';
import palette from '../../../../theme/palette';
import { API_LIMIT_DATA } from '../../../../utils/constants';
import UpcomingTestDetails from './Teacher.UpcomingTestDetails';
import { fetchAllTestByClassId } from '../../../../service/test.service';

const UpcomingTestList = forwardRef(({ handleExamSelected }, ref) => {
  const [selectedTest, setSelectedTest] = useState(null);
  const [upcomingTestList, setUpcomingTest] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const location = useParams();
  const [offset, setOffset] = useState(1);
  const [hasMore, setHasMore] = useState();
  const classId = location.id;
  const classes = useStyles();
  const [isDataLoading, setIsDataLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    refreshData() {
      refetchTestData();
    }
  }));

  const handleSelection = (id) => {
    const findIndex = upcomingTestList.findIndex((ut) => ut.id === id);
    setSelectedTest(findIndex);
    handleExamSelected(true);
  };

  useEffect(() => {
    async function getTest() {
      const res = await fetchAllTestByClassId(classId, API_LIMIT_DATA, offset);
      if (res) {
        setUpcomingTest(res.results);
        setOffset(offset + 1);
        setHasMore(res.results.length === API_LIMIT_DATA);
      }
      setLoading(false);
    }
    getTest();
  }, []);

  const refetchTestData = async () => {
    setLoading(true);
    const res = await fetchAllTestByClassId(classId, API_LIMIT_DATA, 1);
    if (res) {
      setUpcomingTest(res.results);
      setOffset(1 + 1);
      setHasMore(res.results.length === API_LIMIT_DATA);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const fetchNextData = async () => {
    if (hasMore) {
      const res = await fetchAllTestByClassId(classId, API_LIMIT_DATA, offset);
      if (res) {
        setUpcomingTest((preResource) => [...preResource, ...res.results]);
        setHasMore(res.results.length === API_LIMIT_DATA);
        setOffset(offset + 1);
      }
      setIsDataLoading(false);
    }
  };

  const handleDeleteTest = (id) => {
    const findIndex = upcomingTestList.findIndex((ut) => ut.id === id);
    setSelectedTest([
      ...upcomingTestList.slice(0, findIndex),
      ...upcomingTestList.slice(findIndex + 1)
    ]);
    setSelectedTest(null);
    handleExamSelected(false);
  };

  return (
    <>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20% 0' }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line */}
          {upcomingTestList.filter((ut) => ut.isUpcoming).length === 0 ? (
            <div className={classes.emptyResources}>
              <NoExamIcon />
              <h3 style={{ marginBottom: 10 }}>No upcoming test yet!</h3>
              <p style={{ color: palette.grey[600] }}>
                Please add test by clicking the button on right
              </p>
              <p style={{ color: palette.grey[600] }}>
                These test will be sent to all the students
              </p>
            </div>
          ) : (
            <>
              {selectedTest !== null ? (
                <UpcomingTestDetails
                  testDetails={upcomingTestList[selectedTest]}
                  onBack={() => {
                    setSelectedTest(null);
                    handleExamSelected(false);
                  }}
                  handleDeleteTest={handleDeleteTest}
                />
              ) : (
                <Card style={{ maxHeight: '700px', overflowY: 'auto' }} id="scrollableCard">
                  <CardContent>
                    <InfiniteScroll
                      pageStart={0}
                      loadMore={() => {
                        if (hasMore && !isDataLoading) {
                          fetchNextData();
                          setIsDataLoading(true);
                        }
                      }}
                      hasMore={hasMore}
                      getScrollParent={() => document.getElementById('scrollableCard')}
                      loader={
                        <div className="loader" key={0}>
                          Loading ...
                        </div>
                      }
                      useWindow={false}
                    >
                      {upcomingTestList
                        .filter((ut) => ut.isUpcoming)
                        .map((ut) => (
                          <NameAndChangeToScreenCard
                            title={ut.name}
                            subTitle={moment(ut.createdAt).format('ddd, DD MMM, LT')}
                            key={ut.id}
                            onClick={() => handleSelection(ut.id)}
                            icon={<WritingPageIcon color={palette.secondary.main} />}
                          />
                        ))}
                    </InfiniteScroll>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </>
  );
});

const useStyles = makeStyles({
  emptyResources: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 50,
    paddingBottom: 50
  }
});

UpcomingTestList.propTypes = {
  handleExamSelected: PropTypes.func
};

export default UpcomingTestList;
